import React, { useMemo, useState } from 'react'
import styled from '@emotion/styled'

import { BrowserUtils } from '../../utils/siteUtils'
import { FlexDiv, FlexRowDiv, LoadingImage } from '../generic/Basics'
import { greenBase, redBase, highlightBlueBase } from '../../constants/principles'
import { fontSizeXLarge, fontSize2XSmall, fontSizeXSmall, fontSize2XLarge, fontSize3XSmall, fontSize4XSmall, fontSizeMedium } from '../../constants/principles'
import PriceAdvisor from './PriceAdvisor'
import IneligibleModal from './IneligibleDefModal'
import Button, { SmallButton } from '../generic/button'
import VehicleDetailsModal from './VehicleDetailsModal'
import { VehiclePageNumber, LoadingPageNumber, breakpoints } from '../../constants'
import { HurricaneZipCodes } from '../../constants/hurricaneZipCodes'
import OneOwner from '../../assets/Icons/Autocheck/OneOwner.svg'
import MultipleOwners from '../../assets/Icons/Autocheck/MultipleOwners.svg'
import NoAccidents from '../../assets/Icons/Autocheck/NoAccidents.svg'
import MultipleAccidents from '../../assets/Icons/Autocheck/MultipleAccidents.svg'
import PriorRental from '../../assets/Icons/Autocheck/PriorRental.svg'
import NonRental from '../../assets/Icons/Autocheck/NonRental.svg'
import DigitalRetailing from './steps/DigitalRetailing'
import CobrandingDR from './steps/CobrandingDR'
import IneligibleDR from '../generic/IneligibleDR'
import DealerIncentiveBox from './DealerIncentiveBox'
import IneligibleExplanation from './IneligibleExplanation'
import ReceivedBoostBox from './ReceivedBoostBox'
import { formatPrice } from '../../utils/kbbUtils'
import Mileage from '../../components/landing/LandingTabs/Mileage'

const GTM_RESUBMIT_BTN = 'resubmit_btn'
const VERHICLE_HISTORY_REPORT = 'vehicle-history-report'

const noOfferText = (status, isAssigned = false, dealerName = '', dealerIsDirectToKBB = false, dealerIsDirectToKBBv2, offerCode) => {
  const textValues = {
    INELIGIBLE: {
      header: dealerIsDirectToKBBv2 ? "Your vehicle isn't eligible for a Kelley Blue Book® Instant Cash Offer. " : "Your vehicle isn't eligible for a Kelley Blue Book® Instant Cash Offer. But we can still help. "
    },
    kbbReviewNBH: {
      header: `${dealerIsDirectToKBBv2 ? 'Thanks for requesting a Kelley Blue Book® Instant Cash Offer for your vehicle.' : 'A Kelley Blue Book representative needs to review your information, but our offices are currently closed.'}`,
      text: [
        `${dealerIsDirectToKBBv2 ? 'A Kelley Blue Book representative needs to review your information, but our offices are closed.' : 'You should receive an offer on the next business day.'}`,
        `${dealerIsDirectToKBBv2 ? "We'll send you an email when we have an update - usually the next business day." : "We'll send you an email when we have an update. Thanks."}`
      ]
    },
    FR: {
      header: `${dealerIsDirectToKBBv2 ? 'Thanks for requesting a Kelley Blue Book® Instant Cash Offer for your vehicle.' : "A Kelley Blue Book representative is reviewing your vehicle's information. You'll usually have an answer within 3-5 minutes."}`,
      text: ['refresh']
    },
    INSPECTION: {
      header: `To confidently give you an Instant Cash Offer, we need to see your vehicle.${dealerIsDirectToKBB || dealerIsDirectToKBBv2 ? `` : ` Please call ${isAssigned ? dealerName : `a Participating Dealer`} to make an appointment.`}`,
      text: [
        dealerIsDirectToKBB || dealerIsDirectToKBBv2
          ? `Your Kelley Blue Book Specialist will contact you ${dealerIsDirectToKBB ? 'with the nearest location' : 'to schedule a virtual inspection'}.`
          : 'When you arrive for your appointment, the dealer will inspect your vehicle using the same questions you just answered - and will take you step-by-step through the results.',
        `We look forward to ${dealerIsDirectToKBBv2 ? 'working with you' : 'seeing you'}.`
      ]
    },
    EXPIRED: {
      header: 'Your Instant Cash Offer Has Expired.',
      text: ['Please resubmit your offer. You will only need to update the info like mileage or condition that may have changed.', 'Please note: your offer may be higher or lower than the previous amount.']
    },
    EXPIRED_RESUBMIT: {
      header: 'Renew Your Offer',
      text: ['Adjust your mileage and/or use the link below to update other vehicle information.']
    }
  }
  return textValues[status]
}

const defaultText = status => ({
  header: status,
  text: []
})

const HurricaneWarning = ({ textAlign }) => {
  return (
    <TextDiv fontColor={redBase} fontSize={fontSize3XSmall} textAlign={textAlign}>
      {'Due to the historic flooding caused by the recent hurricane, this ZIP code was recently declared a Major Disaster area by '}
      <StyledA href="https://www.fema.gov/" target="_blank">
        FEMA
      </StyledA>
      {
        '. Although we hope your vehicle was not affected, we want to remind you that flood-damaged vehicles and those with salvage titles are not eligible for a Kelley Blue Book® Instant Cash Offer. We wish you all the best as you recover from this tragedy.'
      }
    </TextDiv>
  )
}

const CongratsText = ({ firstName, isAdmin, isAssigned, dealerName }) => (
  <>
    <HeaderText data-testid={`oc-txt-header-confirm-msg`}>Congratulations, {firstName}!</HeaderText>
    {!isAdmin && <TextDiv>{!isAssigned ? `Participating Dealers are ` : `${dealerName} is `}interested in your</TextDiv>}
  </>
)

const NoOfferDiv = ({ status, expired, year, make, model, showWarning, offerId, canResubmit, isAssigned, isDigitalRetail, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, updateGlobalState, offerCode }) => (
  <NoOfferMainDiv>
    <MediumHeaderText data-testid={`oc-txt-header-confirm-msg`}>
      {(noOfferText(status, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, offerCode) || defaultText(status)).header} {isDigitalRetail && status === 'INELIGIBLE' && 'But the dealer might still take it as a trade in.'}
    </MediumHeaderText>
    {(noOfferText(expired ? 'EXPIRED' : status, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, offerCode) || defaultText(status)).text && (
      <>
        {showWarning && status !== 'INELIGIBLE' && (
          <>
            <HurricaneWarning />
            <br />
          </>
        )}
        {(noOfferText(expired ? 'EXPIRED' : status, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, offerCode) || defaultText(status)).text.map((txt, i) => {
          if (txt === 'refresh') return <Refresh key={i} offerId={offerId} dealerIsDirectToKBBv2={dealerIsDirectToKBBv2} />
          else return <div key={i}>{txt}</div>
        })}
        <br />
      </>
    )}
    {expired && canResubmit && (
      <>
        <Button
          data-testid={`oc-btn-resubmit`}
          onClick={e => {
            updateGlobalState({ fireAnalytics: GTM_RESUBMIT_BTN })
            BrowserUtils.navigateWithUrlState({ year, make, model, updateState: { p: VehiclePageNumber, lp: VehiclePageNumber } })
          }}>
          Resubmit
        </Button>
        <br />
        <br />
      </>
    )}
  </NoOfferMainDiv>
)

const NoOfferForResubmitDiv = ({
  vehicleInfo,
  status,
  expired,
  year,
  make,
  model,
  showWarning,
  offerId,
  canResubmit,
  isAssigned,
  isDigitalRetail,
  dealerName,
  dealerIsDirectToKBB,
  dealerIsDirectToKBBv2,
  updateGlobalState,
  resubmitButtonClick,
  offerCode,
  ...props
}) => (
  <NoOfferMainDiv>
    <HeaderText data-testid={`oc-txt-header-confirm-msg`}>
      {(noOfferText(status, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, offerCode) || defaultText(status)).header} {isDigitalRetail && status === 'INELIGIBLE' && 'But the dealer might still take it as a trade in.'}
    </HeaderText>
    {(noOfferText(expired ? 'EXPIRED_RESUBMIT' : status, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, offerCode) || defaultText(status)).text && (
      <>
        {showWarning && status !== 'INELIGIBLE' && (
          <>
            <HurricaneWarning />
            <br />
          </>
        )}
        {(noOfferText(expired ? 'EXPIRED_RESUBMIT' : status, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, offerCode) || defaultText(status)).text.map((txt, i) => {
          if (txt === 'refresh') return <Refresh key={i} offerId={offerId} dealerIsDirectToKBBv2={dealerIsDirectToKBBv2} />
          else
            return (
              <TextDiv textAlign="left" fontWeight="600" key={i}>
                {txt}
              </TextDiv>
            )
        })}
        <br />
      </>
    )}
    {expired && <ResubmitNewPage {...{ year, mileage: vehicleInfo.mileage, make, model, vehicleInfo, canResubmit, updateGlobalState, resubmitButtonClick }} {...props} />}
  </NoOfferMainDiv>
)

const ResubmitNewPage = ({ year, mileage, make, model, vehicleInfo, canResubmit, updateGlobalState, resubmitButtonClick, ...props }) => {
  const navigateWithState = (updateState, state, initPath) => BrowserUtils.navigateWithUrlState({ year: year, make: make, model: model, updateState }, state, initPath)
  const updateVehiclePropNumber = (propName, propValue) => {
    if (!isNaN(+propValue)) {
      navigateWithState({ [propName.charAt(0)]: +propValue })
    }
  }
  const handleMileage = newValue => {
    if (!isNaN(newValue)) {
      updateVehiclePropNumber('mileage', newValue)
    }
  }
  return (
    <YourVehicleDiv>
      <MediumHeaderText paddingBottom="0px" textAlign="left">
        Your Vehicle
      </MediumHeaderText>
      <TextDiv textAlign="left" fontWeight="600">
        {year && year.displayName.concat(' ', make && make.displayName, ' ', model && model.displayName)}
      </TextDiv>
      <TextDiv textAlign="left" fontWeight="600">
        Style: {vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.vehicleDescription && vehicleInfo.vehicle.vehicleDescription.trim}
      </TextDiv>
      <TextDiv textAlign="left" fontWeight="600">
        Engine: {vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.vehicleDescription && vehicleInfo.vehicle.vehicleDescription.engine}
      </TextDiv>
      <TextDiv textAlign="left" fontWeight="600">
        Transmission: {vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.vehicleDescription && vehicleInfo.vehicle.vehicleDescription.transmission}
      </TextDiv>
      <TextDiv textAlign="left" fontWeight="600">
        Drivetrain: {vehicleInfo && vehicleInfo.vehicle && vehicleInfo.vehicle.vehicleDescription && vehicleInfo.vehicle.vehicleDescription.drivetrain}
      </TextDiv>
      <br></br>
      <MileageDiv>
        <Mileage isError={!mileage} inputMileage={mileage} handleMileage={handleMileage} isDisabled={canResubmit ? false : true} {...props} />
      </MileageDiv>
      <br></br>
      {!(!mileage || mileage > 300000) && canResubmit && (
        <A
          data-testid={`oc-link-update-vehicle`}
          onClick={e => {
            updateGlobalState({ fireAnalytics: GTM_RESUBMIT_BTN })
            BrowserUtils.navigateWithUrlState({ year, make, model, updateState: { p: VehiclePageNumber, lp: VehiclePageNumber, m: mileage } })
          }}>
          Update items about your vehicle like Condition, Options, and more.
        </A>
      )}
      {!(!mileage || mileage > 300000) && canResubmit && (
        <ButtonDiv>
          <Button data-testid={`oc-btn-resubmit`} onClick={e => resubmitButtonClick()}>
            Renew Your Offer
          </Button>
          <br />
        </ButtonDiv>
      )}
    </YourVehicleDiv>
  )
}

const Refresh = ({ i, offerId, dealerIsDirectToKBBv2 = false }) => {
  return dealerIsDirectToKBBv2 ? (
    <div key={i}>
      <div>{'A Kelley Blue Book representative is still reviewing your information.'}</div>
      <div>{"We'll send you an email when we have an update."}</div>
    </div>
  ) : (
    <div key={i}>
      {"We'll send you an email when we have an update, or you can "}
      <StyledA href={__PATH_PREFIX__ + '/offer/' + offerId + '/refresh' + Date.now() + '/' + BrowserUtils.getInitParams().initialQueryString}>click here</StyledA>
      {' to refresh this page to see the status.'}
    </div>
  )
}

const Vehicle = ({ year, make, model, trim, images, isDesktop, setShowModal, vehicleInfo, isAdmin, displayImages }) => (
  <StartAlignDiv>
    <TextDiv fontSize={fontSize2XSmall}>
      {year && year.displayName} {make && make.displayName} {model && model.displayName} {trim && trim.displayName}
    </TextDiv>
    <VehicleDetailsModal vehicleInfo={vehicleInfo} setShowModal={setShowModal} isAdmin={isAdmin} />
    <StyledLink onClick={() => setShowModal('vehicleDetails')}>Review Vehicle Details</StyledLink>
    <LoadingImage displayImages={displayImages} images={images} spinnerSize={50} imgWidth={isDesktop ? 330 : 200} width={isDesktop ? 330 : 200} height={isDesktop ? 190 : 142} imgMarginTop={isDesktop ? -35 : -15} marginBottom={isDesktop ? -30 : 0} />
  </StartAlignDiv>
)
const OfferInfo = ({ offer, dealerIsDirectToKBB }) => {
  const showExpiration = offer && (offer.status === 'INSPECTION' || offer.status === 'CLEARED' || offer.status === 'CONFIRMED' || offer.status === 'AUCTION')
  const offerExpiration = offer && showExpiration ? offer.expirationDate.split('T')[0].split('-') : undefined
  const formatedExpiration = offerExpiration && `${offerExpiration[1]}/${offerExpiration[2]}/${offerExpiration[0]}`

  return (
    <>
      {offerExpiration && (
        <TextDiv fontSize={fontSize3XSmall} fontColor={redBase} dealerIsDirectToKBB={dealerIsDirectToKBB}>
          Offer good until: {formatedExpiration}
        </TextDiv>
      )}
      <SmallTextDiv data-testid={`oc-txt-offer-id`}>Offer ID: {offer.offerId}</SmallTextDiv>
      <SmallTextDiv data-testid={`oc-txt-offer-email`}>Email: {offer.prospect.contactInfo.email}</SmallTextDiv>
      <SmallTextDiv data-testid={`oc-txt-offer-zipcode`}>Zip Code: {offer.prospect.contactInfo.zip}</SmallTextDiv>
    </>
  )
}

export default ({
  year,
  make,
  model,
  trim,
  images,
  offer,
  isDesktop,
  setShowModal,
  isAdmin,
  canResubmit,
  displayImages,
  isAssigned,
  isDigitalRetail,
  dealerName,
  isCobrandDR,
  autoCheckData,
  qxData,
  setOpenReqAppOverlay,
  disableRequestBtn,
  appointmentDate,
  dealerGuid,
  isCombinedOffer,
  updateGlobalState = v => v,
  dealerIsDirectToKBB,
  dealerIsDirectToKBBv2,
  isUIForWC,
  isShowCustomerDealerList,
  nextClick,
  ...props
}) => {
  const offerExpired = offer.status === 'EXPIRED' || offer.isExpired
  const showOffer = offer && (offer.status === 'CLEARED' || offer.status === 'CONFIRMED' || offer.status === 'AUCTION' || offer.status === 'GROUNDED' || offer.status === 'PURCHASED' || offer.status === 'RECEIVED')
  const runUntilDate = new Date('December 23 2020')
  const isHurricaneZip = HurricaneZipCodes.includes(parseInt(offer?.prospect?.contactInfo?.zip))
  const showWarning = isHurricaneZip && (!runUntilDate || new Date() < runUntilDate)
  const { hasAccidents, isOriginalOwner, usedAsRentalCar, reportDate } = autoCheckData || {}
  const dealerAnswered = offer?.accidentQuestionModifiedByDealer
  let vin = offer && offer.prospect.vehicle.vin
  let hasMarkup = isAssigned && dealerGuid && offer.markupAmounts && offer.markupAmounts[dealerGuid] !== undefined && offer.markupAmounts[dealerGuid] > 0
  let markupAmount
  if (hasMarkup) {
    markupAmount = offer.markupAmounts[dealerGuid]
  }
  let vehicleInfo = offer && offer.prospect
  //grab modifications to add back into conditions array if we have
  let mods = vehicleInfo.conditions.filter(x => x.section === 'Aftermarket')
  // Update condition tier selection question
  if (offer && vehicleInfo && offer.conditionTierList && offer.conditionTierSelection) {
    const conditionTierQuestion = {
      questionId: 'qc/ConditionTierSelection',
      item: offer.conditionTierSelection,
      label: offer.conditionTierList.find(c => c.name === offer.conditionTierSelection).description,
      section: 'Vehicle Condition',
      subsection: 'Condition Tier Selection',
      questionType: 'ToggleSelect',
      value: offer.conditionTierList.find(c => c.name === offer.conditionTierSelection).description,
      acceptsComment: false,
      comment: ''
    }
    vehicleInfo = { ...vehicleInfo, conditions: [...mods, conditionTierQuestion] }
  }

  const haveAtLeastOneAcceptApptRequest = useMemo(() => {
    // Show top Request Appointment button when have at least a Dealer is enabled Appt Requests
    const dealers = offer.prospect.dealershipsAndInventory
    const appointments = offer.appointments
    const replacementVehicle = offer.prospect.replacementVehicle
    const hasRv = replacementVehicle.find(question => question.label === 'LeadType').value === 'OptIn'
    const moreDealers = !isAssigned ? (hasRv ? dealers.buyingCenters : dealers.buyingCenters.concat(dealers.tradeInCenters)) : []
    const dls = dealers.allocationEntries.concat(moreDealers)
    for (var i = 0; i < dls.length; i++) {
      const appointment = appointments?.find(appointment => appointment.dealerId === dls[i].dealerInfo?.dealerId || appointment.dealerId === dls[i].dealerId)
      if (appointment && appointment.acceptAppointmentRequests) return true
    }
    return false
  }, [offer.prospect.dealershipsAndInventory, offer.appointments])

  const CTAButton = () =>
    offer.isAppointmentRequestEnabled &&
    haveAtLeastOneAcceptApptRequest &&
    !offerExpired &&
    !isDigitalRetail &&
    !isCobrandDR &&
    !isAdmin && (
      <SmallButton
        data-testid={`oc-btn-request-appointment`}
        onClick={() => {
          if (isCombinedOffer) {
            if (BrowserUtils.isIframe() && window.parent.document.getElementById('icoCertificate')) {
              if (window.parent.innerHeight < window.innerHeight) {
                const iframeOffset = window.parent.document.getElementById('icoCertificate').offsetTop
                const step1Offset = document.getElementById('step-1').offsetTop
                window.parent.scrollTo(0, iframeOffset + step1Offset)
              }
            }
          } else {
            if (typeof window !== 'undefined' && typeof document !== 'undefined') {
              const step1 = document.getElementById('step-1')
              if (step1) {
                window.scrollTo(0, step1.offsetTop)
              }
            }
          }
          updateGlobalState({ fireAnalytics: 'RequestAppt_Top' })
        }}>
        Request Appointment
      </SmallButton>
    )

  const resubmitButtonClick = () => {
    setTimeout(
      (updateState, taggingEvent) => {
        updateState({ fireAnalytics: GTM_RESUBMIT_BTN })
      },
      500,
      updateGlobalState,
      GTM_RESUBMIT_BTN
    )
    updateGlobalState({
      firstName: offer?.prospect?.contactInfo.firstName,
      lastName: offer?.prospect?.contactInfo.lastName,
      email: offer?.prospect?.contactInfo.email,
      phone: offer?.prospect?.contactInfo.phone,
      zip: offer?.prospect?.contactInfo.zip,
      termsAccepted: true
    })

    nextClick(LoadingPageNumber)
  }

  const { initialPathParams, initialQueryParams } = BrowserUtils.getInitParams()

  const questions =
    initialQueryParams.utm_source && initialQueryParams.utm_source === 'email' && offer && offer.offerCode && offer.offerCode.toUpperCase() === 'TC' ? (
      <div></div>
    ) : dealerIsDirectToKBBv2 ? (
      <QuestionsV2>
        Questions about the Kelley Blue Book Instant Cash
        <br /> Offer program? Call <StyledA href="tel:513-572-1181">(513) 572-1181</StyledA>
      </QuestionsV2>
    ) : (
      <Questions data-testid={`oc-txt-kbb-question`}>Questions about the Instant Cash Offer program? Call {isAdmin ? <StyledA href="tel:877-547-7780">(877) 547-7780</StyledA> : <StyledA href="tel:866-559-5268">(866) 559-5268</StyledA>} </Questions>
    )

  return offer ? (
    <>
      {!offerExpired && hasMarkup && <ReceivedBoostBox dealerName={dealerName} markupAmount={markupAmount} />}
      <MainDiv certificate={showOffer}>
        {offerExpired ? (
          <>
            {isAdmin ? (
              <>
                <NoOfferDiv
                  status={'EXPIRED'}
                  expired={true}
                  {...{ year, make, model, canResubmit, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, updateGlobalState }}
                  showWarning={showWarning}
                  offerId={offer.offerId}
                  offerCode={offer.offerCode}
                />
                <FullWidth width="auto">
                  <Vehicle {...{ year, make, model, trim, images, isDesktop, setShowModal, vehicleInfo, isAdmin, displayImages }} />
                </FullWidth>
              </>
            ) : (
              <NoOfferForResubmitDiv
                status={'EXPIRED_RESUBMIT'}
                expired={true}
                {...{ vehicleInfo, year, make, model, canResubmit, isAssigned, dealerName, dealerIsDirectToKBB, dealerIsDirectToKBBv2, updateGlobalState }}
                showWarning={showWarning}
                offerId={offer.offerId}
                offerCode={offer.offerCode}
                {...props}
                resubmitButtonClick={resubmitButtonClick}
              />
            )}
          </>
        ) : showOffer ? (
          <>
            <CongratsDiv>
              <CongratsText firstName={offer.prospect.contactInfo.firstName} isAdmin={isAdmin} isAssigned={isAssigned} dealerName={dealerName} />
              <Vehicle {...{ year, make, model, trim, images, isDesktop, setShowModal, vehicleInfo, isAdmin, displayImages }} />
              {!offerExpired && showOffer && hasMarkup && isDesktop && (
                <DealerIncentiveWrapper>
                  <DealerIncentiveBox {...{ dealerName, offerAmount: offer.amount, markupAmount, isDesktop }} />
                </DealerIncentiveWrapper>
              )}
            </CongratsDiv>
            <OfferDiv>
              <TextDiv data-testid={`oc-txt-offer-yourkbbico`} fontSize={fontSizeXSmall}>
                Your Kelley Blue Book Instant Cash <SpanRelative>Offer{markupAmount && <BadgeText>1</BadgeText>}</SpanRelative>
                {markupAmount && (
                  <>
                    {' '}
                    <SpanRelative>
                      + Dealer Boost<BadgeText>2</BadgeText>
                    </SpanRelative>
                  </>
                )}
              </TextDiv>
              <TextDiv data-testid={`oc-txt-offer-amount`} fontSize={fontSize2XLarge} fontColor={greenBase}>
                {formatPrice(offer.amount + (markupAmount ?? 0))}
              </TextDiv>
              {showWarning && (
                <>
                  <HurricaneWarning textAlign={'right'} />
                  <br />
                </>
              )}
              {!dealerIsDirectToKBB && !dealerIsDirectToKBBv2 && (
                <LegalTextDiv>This offer can only be redeemed {isUIForWC ? 'with' : 'at'} a Participating Dealer. Kelley Blue Book doesn't redeem offers, purchase vehicles, or own/operate Participating Dealers.</LegalTextDiv>
              )}
              <OfferInfo offer={offer} dealerIsDirectToKBB={dealerIsDirectToKBB || dealerIsDirectToKBBv2} />
              {isShowCustomerDealerList && <CTAButton />}
              {!offerExpired && showOffer && hasMarkup && !isDesktop && (
                <DealerIncentiveWrapper>
                  <DealerIncentiveBox {...{ dealerName, offerAmount: offer.amount, markupAmount, isDesktop }} />
                </DealerIncentiveWrapper>
              )}
              {!offerExpired && offer && (
                <>
                  {isDigitalRetail && offer.status === 'CLEARED' && !isCobrandDR && <DigitalRetailing offer={offer} markupAmount={markupAmount} />}
                  {isCobrandDR && <CobrandingDR isCleared={offer && offer.status === 'CLEARED'} dealerListUrl={offer.prospect.cobranding.dealerListUrl} offer={offer} markupAmount={markupAmount} />}
                </>
              )}
            </OfferDiv>
          </>
        ) : (
          <>
            <NoOfferDiv
              status={offer.status}
              expired={offer.isExpired}
              offerId={offer.offerId}
              offerCode={offer.offerCode}
              {...{ isAssigned, dealerName, isDigitalRetail, showWarning, dealerIsDirectToKBB, dealerIsDirectToKBBv2, updateGlobalState }}
              {...props}
            />
            <FullWidth>
              <Vehicle {...{ year, make, model, trim, images, isDesktop, setShowModal, vehicleInfo, isAdmin, displayImages }} />
              <EndAlignDiv>
                {offer.status === 'INELIGIBLE' && (
                  <>
                    <IneligibleModal setShowModal={setShowModal} />
                    {dealerIsDirectToKBBv2 ? null : (
                      <IneligibleLink data-testid={`oc-txt-what-make-ineligible`} onClick={e => setShowModal('ineligibleDef')}>
                        What makes a vehicle ineligible?
                      </IneligibleLink>
                    )}
                  </>
                )}
                <OfferInfo offer={offer} />
                {offer.status === 'INSPECTION' && !isUIForWC && <CTAButton />}
                {offer.status !== 'INSPECTION' && questions}
              </EndAlignDiv>
            </FullWidth>
            {!offerExpired && isCobrandDR && offer && <CobrandingDR isCleared={offer && offer.status === 'CLEARED'} dealerListUrl={offer.prospect.cobranding.dealerListUrl} offer={offer} markupAmount={markupAmount} />}
          </>
        )}
      </MainDiv>
      {offer.status === 'INELIGIBLE' && dealerIsDirectToKBBv2 && <IneligibleExplanation />}
      {offer.showAdjusterNotes && <AdjusterNotesDiv>Thanks for your patience. A Kelley Blue Book representative needed to review your information.</AdjusterNotesDiv>}
      {!offerExpired && isDigitalRetail && offer && offer.status !== 'CLEARED' && (
        <IneligibleDR reason={'offer was deemed not eligible.'} vin={offer && offer.prospect.vehicle.vin} year={year && year.yearId} make={make && make.makeId} model={model && model.modelId} trim={trim && trim.trimId} />
      )}
      {autoCheckData && !offerExpired && (
        <>
          <AutoCheckBannerContainerMobile>
            <AutoCheckInfoContainer>
              <>
                <div className="autocheck-info-container">
                  <div data-testid={`oc-autocheck-info-title`} className="autocheck-info-container--title">
                    Vehicle History Report
                  </div>
                  <div data-testid={`oc-autocheck-info-vin`} className="autocheck-info-container--text">
                    VIN: {offer && vin}
                  </div>
                  <div data-testid={`oc-autocheck-info-generation-time`} className="autocheck-info-container--text">
                    Report Generated On: {reportDate}
                  </div>
                </div>
              </>
            </AutoCheckInfoContainer>
            <AutoCheckIconContainer>
              <StyledImg src={isOriginalOwner ? OneOwner : MultipleOwners} />
              {!dealerAnswered && <StyledImg src={hasAccidents ? MultipleAccidents : NoAccidents} />}
              <StyledImg src={usedAsRentalCar ? PriorRental : NonRental} className={usedAsRentalCar ? 'non-rental' : ''} />
            </AutoCheckIconContainer>
            <AutoCheckDisclaimer>(This is not a full history report)</AutoCheckDisclaimer>
            <AutoCheckLinkContainer className="autocheck-info-container--link">
              <span data-testid={`oc-autocheck-info-sign`}>Information provided by AutoCheck&reg;</span>
              <br />
              <a
                href={`https://www.autocheck.com/vehiclehistory/?siteID=5149&vin=${vin}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  updateGlobalState({ fireAnalytics: VERHICLE_HISTORY_REPORT })
                }}>
                Get Full Report
              </a>
            </AutoCheckLinkContainer>
          </AutoCheckBannerContainerMobile>
        </>
      )}
      {autoCheckData && !offerExpired && (
        <AutoCheckBannerContainerDesktop>
          <AutoCheckInfoContainer>
            <>
              <div className="autocheck-info-container">
                <div className="autocheck-info-container--title" data-testid={`oc-autocheck-info-title`}>
                  Vehicle History Report
                </div>
                <div className="autocheck-info-container--text" data-testid={`oc-autocheck-info-vin`}>
                  VIN: {offer && vin}
                </div>
                <div className="autocheck-info-container--text" data-testid={`oc-autocheck-info-generation-time`}>
                  Report Generated On: {reportDate}
                </div>
                <AutoCheckLinkContainer className="autocheck-info-container--link">
                  <span>Information provided by AutoCheck&reg;</span>
                  <br />
                  <a
                    href={`https://www.autocheck.com/vehiclehistory/?siteID=5149&vin=${vin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      updateGlobalState({ fireAnalytics: VERHICLE_HISTORY_REPORT })
                    }}>
                    Get Full Report
                  </a>
                </AutoCheckLinkContainer>
              </div>
            </>
          </AutoCheckInfoContainer>
          <AutoCheckIconContainer>
            <AutoCheckIcons>
              <StyledImg data-testid={`oc-ico-multi-owner`} src={isOriginalOwner ? OneOwner : MultipleOwners} />
              {!dealerAnswered && <StyledImg data-testid={`oc-ico-accidents`} src={hasAccidents ? MultipleAccidents : NoAccidents} />}
              <StyledImg data-testid={`oc-ico-rental-car`} src={usedAsRentalCar ? PriorRental : NonRental} className={usedAsRentalCar ? 'non-rental' : ''} />
            </AutoCheckIcons>
            <AutoCheckDisclaimer data-testid={`oc-autocheck-disclaim`}>(This is not a full history report)</AutoCheckDisclaimer>
          </AutoCheckIconContainer>
        </AutoCheckBannerContainerDesktop>
      )}
      {!dealerIsDirectToKBBv2 && !offerExpired && showOffer && offer.priceAdvisor && <PriceAdvisor isDesktop={isDesktop} url={offer.priceAdvisor} dealerIsDirectToKBB={dealerIsDirectToKBB} />}
    </>
  ) : (
    ''
  )
}

const CongratsDiv = styled(FlexDiv)`
  font-size: ${fontSize2XSmall};
  align-items: center;
  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
    margin: 8px 16px 0 16px;
  }
  margin: 8px 0 0 0;
  width: 100%;
`
const AdjusterNotesDiv = styled(FlexDiv)`
  background-color: #f1f1f1;
  font-weight: bold;
  font-size: ${fontSize2XSmall};
  padding: 16px 8px;
`
const StyledLink = styled.div`
  color: ${highlightBlueBase};
  font-size: ${fontSize2XSmall};
  text-decoration: none;
  :hover {
    text-decoration: underline;
  }
  padding-bottom: 16px;
  cursor: pointer;
  @media (min-width: ${breakpoints.lg}px) {
    z-index: 1;
  }
`
const OfferDiv = styled(FlexDiv)`
  max-width: 460px;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  align-self: center;
  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-end;
    text-align: right;
    align-self: flex-start;
    margin: 0 16px;
  }
  width: 100%;
`
const EndAlignDiv = styled(FlexDiv)`
  align-items: center;
  justify-content: 'center';
  @media (min-width: ${breakpoints.lg}px) {
    justify-content: flex-start;
    align-items: flex-end;
  }
`

const StartAlignDiv = styled(FlexDiv)`
  align-items: ${p => (p.isDesktop ? p.align : 'center')};
  justify-content: 'center';
  @media (min-width: ${breakpoints.lg}px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
`

const FullWidth = styled(FlexRowDiv)`
  flex-direction: column;
  width: ${p => p.width || '100%'};
  justify-content: space-between;
  align-items: center;
  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
    flex-direction: row;
  }
`
const MainDiv = styled(FlexRowDiv)`
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  padding-top: 24px;
  align-items: center;
  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
    flex-direction: ${p => (p.certificate !== true ? 'column' : 'row')};
    flex-wrap: nowrap;
  }
`
const TextDiv = styled.div`
  font-weight: ${p => (p.fontWeight ? p.fontWeight : 'bold')};
  padding-top: ${p => (p.dealerIsDirectToKBB ? '4px' : `8px`)};
  font-size: ${p => p.fontSize};
  color: ${p => p.fontColor};
  text-align: ${p => (p.textAlign ? p.textAlign : 'center')};
  @media (min-width: ${breakpoints.lg}px) {
    text-align: ${p => (p.textAlign ? p.textAlign : `initial`)};
  }
`

const NoOfferMainDiv = styled.div`
  font-weight: 'normal';
  font-size: ${fontSize2XSmall};
  text-align: center;
  width: 100%;
  @media (min-width: ${breakpoints.lg}px) {
    text-align: left;
  }
`
const SmallTextDiv = styled.div`
  font-size: ${fontSize3XSmall};
  font-weight: normal;
  line-height: 18px;
`
const Questions = styled.div`
  padding-top: 40px;
  font-size: ${fontSize2XSmall};
  font-weight: bold;
  text-align: center;
`
const QuestionsV2 = styled.div`
  padding-top: 80px;
  font-size: ${fontSize2XSmall};
  font-weight: normal;
  text-align: right;
`
const YourVehicleDiv = styled.div`
  position: relative;
  padding: 35px;
  background: #ffffff;
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  @media (max-width: ${breakpoints.lg}px) {
    padding: 15px 15px 25px;
  }
`
const ButtonDiv = styled.div`
  text-align: right;
  bottom: 8px;
  right: 16px;
  @media (max-width: ${breakpoints.lg}px) {
    text-align: center;
  }
`
const MileageDiv = styled.div`
  width: 50%;
  @media (max-width: ${breakpoints.lg}px) {
    width: 100%;
    text-align: center;
  }
`
const LegalTextDiv = styled.div`
  font-size: ${fontSize4XSmall};
  font-weight: normal;
  text-align: center;
  @media (min-width: ${breakpoints.lg}px) {
    text-align: right;
  }
  padding-bottom: 16px;
  width: 100%;
`
const HeaderText = styled.div`
  font-size: ${fontSizeXLarge};
  font-weight: bold;
  padding-bottom: 16px;
  text-align: center;
  @media (min-width: ${breakpoints.lg}px) {
    text-align: initial;
  }
  line-height: 1.25;
  width: 100%;
`
const MediumHeaderText = styled(HeaderText)`
  font-size: ${fontSizeMedium};
  padding-bottom: ${p => (p.paddingBottom ? p.paddingBottom : '32px')};
  max-width: 840px;
  text-align: ${p => (p.textAlign ? p.textAlign : 'center')};
`
const IneligibleLink = styled.span`
  font-size: ${fontSize2XSmall};
  text-decoration: none;
  color: ${highlightBlueBase};
  padding-bottom: 40px;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`
const StyledA = styled.a`
  color: ${highlightBlueBase};
  text-decoration: none;
  :hover {
    text-decoration: underline;
  }
`
const AutoCheckBannerContainerDesktop = styled.div`
  display: none;
  @media (min-width: ${breakpoints.lg}px) {
    width: 100%;
    display: flex;
    flex-direction: row;
    margin-top: 40px;
  }
`
const AutoCheckBannerContainerMobile = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 1px solid #c3c3c3;
  padding-top: 32px;
  margin-top: 40px;
  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`
const AutoCheckInfoContainer = styled.div`
  width: 100%;
  flex-direction: column;
  .autocheck-info-container {
    display: flex;
    flex-direction: column;
    border: none;
    align-items: center;
    text-align: center;
    padding-right: 0;
    .autocheck-info-container--title {
      font-size: 18px;
      font-weight: 700;
    }
    .autocheck-info-container--text {
      font-size: 12px;
    }
  }
  @media (min-width: ${breakpoints.lg}px) {
    width: 25%;
    display: flex;
    padding: 15px;
    .autocheck-info-container {
      display: flex;
      flex-direction: column;
      border: none;
      align-items: flex-start;
      padding-right: 15;
      .autocheck-info-container--title {
        font-size: 18px;
        font-weight: 700;
      }
      .autocheck-info-container--text {
        font-size: 12px;
      }
    }
  }
`
const AutoCheckIconContainer = styled.div`
  display: flex;
  width: 100%;
  margin-top: 35px;
  justify-content: space-evenly;
  flex-wrap: wrap;
  .non-rental {
    margin-left: -15px;
  }
  @media (max-width: 383px) {
    .non-rental {
      margin-left: 0;
    }
  }
  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
    width: 50%;
    padding: 15px;
    margin-top: 0;
    justify-content: flex-start;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: center;
  }
`
const AutoCheckIcons = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
`
const AutoCheckDisclaimer = styled.span`
  font-size: 12px;
  margin-top: 15px;
`
const AutoCheckLinkContainer = styled.div`
  margin-top: 50px;
  font-size: 12px;
  text-align: center;
  a {
    color: ${highlightBlueBase};
    text-decoration: none;
    :hover {
      text-decoration: underline;
    }
    cursor: pointer;
    @media (min-width: ${breakpoints.lg}px) {
      z-index: 1;
    }
  }

  @media (min-width: ${breakpoints.lg}px) {
    margin-top: 5px;
    font-size: 10px;
  }
`
const StyledImg = styled.img`
  height: 60px;
`
const DealerIncentiveWrapper = styled(FlexRowDiv)`
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  padding-top: 10px;
  align-items: center;
  margin-top: 10px;
  flex-wrap: nowrap;
  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
    flex-direction: ${p => (p.certificate !== true ? 'column' : 'row')};
  }
`
const BadgeText = styled.div`
  font-size: 10px;
  position: absolute;
  top: -3px;
  right: -5px;
  color: #ccc;
`
const SpanRelative = styled.span`
  position: relative;
  display: inline-block;
`
const A = styled.a`
  text-decoration: none;
  color: ${highlightBlueBase};
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`
